import {
  CargoClaimDetailRow,
  CargoClaimDocumentRow,
  CargoClaimHistoryRow,
  CargoClaimLineRow,
  CargoClaimRow,
} from './api/api.types';
import {
  CargoClaim,
  ClaimDocument,
  ClaimHistoryEntry,
  ClaimLine,
} from './CargoClaims.types';

/*
 * The wire row as the screen's claim.
 *
 * Kept as a named step rather than renaming the screen's fields to match the
 * service: the grid, the detail panel and the fixtures behind the detail route
 * all read `shipmentNumber`, and renaming them to `shipmentNo` would touch far
 * more than it earns.
 *
 * `amount` arrives as a string because MySQL DECIMAL loses precision as a JSON
 * number. It is parsed here, once, so nothing downstream has to remember —
 * a null or unparseable amount becomes 0 rather than NaN, which formats as
 * "NaN" on screen and looks like a bug in the claim rather than a missing
 * value.
 *
 * The three collections are empty by construction: the list route does not
 * carry them, and the detail route is what fills them in.
 */
export default function toCargoClaim(row: CargoClaimRow): CargoClaim {
  const amount = Number(row.amount);

  return {
    reference: row.reference,
    company: row.company,
    brand: row.brand ?? '',
    shipmentNumber: row.shipmentNo,
    hawbNumber: row.hawbNo ?? '',
    currentLocation: row.currentLocation ?? '',
    natureOfLoss: row.natureOfLoss ?? '',
    amount: Number.isFinite(amount) ? amount : 0,
    currency: row.currency ?? '',
    claimDate: row.claimDate ?? '',
    notifyingDate: row.notifyingDate ?? '',
    contactPerson: row.contactPerson ?? '',
    source: row.source,
    eligible: row.eligible ?? false,
    companyRecognised: row.companyRecognised,
    status: row.status,
    lines: [],
    documents: [],
    history: [],
  };
}

/** Decimals arrive as strings. Absent reads as 0 rather than NaN. */
function toNumber(value: string | number | null): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

/*
 * The detail read, which returns the row rather than the partner projection,
 * so the credit note columns and the collections are all present.
 */
export function toCargoClaimDetail(
  row: CargoClaimDetailRow,
  lines: CargoClaimLineRow[],
  documents: CargoClaimDocumentRow[],
  history: CargoClaimHistoryRow[]
): CargoClaim {
  return {
    ...toCargoClaim(row),
    /*
     * Only when there is a number. A credit note keyed off the invoice number
     * alone would render an empty panel for a claim that has none.
     */
    creditNote: row.creditNoteNumber
      ? {
          invoiceNumber: row.creditNoteNumber,
          date: row.creditNoteDate ?? '',
          amount: toNumber(row.creditNoteAmount),
          currency: row.creditNoteCurrency ?? '',
        }
      : undefined,
    lines: lines.map(
      (line): ClaimLine => ({
        businessUnit: line.businessUnit,
        description: line.description ?? '',
        quantity: line.quantity ?? 0,
        fobAmount: toNumber(line.fobAmount),
        currency: line.currency ?? '',
      })
    ),
    documents: documents.map(
      (document): ClaimDocument => ({
        id: document.id,
        type: document.type,
        fileName: document.filename,
        // Bytes on the wire, kilobytes on screen.
        sizeKb: Math.round(toNumber(document.sizeBytes) / 1024),
        uploadedAt: document.uploadedAt ?? '',
      })
    ),
    history: history.map(
      (entry): ClaimHistoryEntry => ({
        from: entry.fromStatus,
        to: entry.toStatus,
        actorType: entry.actorType,
        // The column holds an id; there is no route that turns it into a name.
        actor: entry.actorId ?? entry.actorType,
        reason: entry.reason ?? undefined,
        at: entry.createdAt,
      })
    ),
  };
}
